import type { LngLat, MapLocation } from './types';

export type TravelMode = 'walking' | 'driving';

export function isTravelMode(value: unknown): value is TravelMode {
  return value === 'walking' || value === 'driving';
}

/** Where a route starts: another location, or the user's current position. */
export type Origin =
  | { kind: 'location'; location: MapLocation }
  | { kind: 'here'; lngLat: LngLat | null; status: 'locating' | 'ready' | 'error'; message?: string };

/** Routes start and end at a building's entrance when Directus has one. */
export function doorOf(location: MapLocation, accessible = false): LngLat {
  return (accessible ? location.accessibleEntrance : null) ?? location.entrance ?? location.lngLat;
}

export function originLngLat(origin: Origin | null): LngLat | null {
  if (!origin) return null;
  return origin.kind === 'location' ? doorOf(origin.location) : origin.lngLat;
}

export interface RouteStep {
  instruction: string;
  /** Meters until the next step. */
  distance: number;
  type: string;
  modifier?: string;
}

export interface Route {
  mode: TravelMode;
  distance: number; // meters
  duration: number; // seconds
  geometry: GeoJSON.LineString;
  steps: RouteStep[];
}

interface MapboxDirectionsResponse {
  code: string;
  message?: string;
  routes?: {
    distance: number;
    duration: number;
    geometry: GeoJSON.LineString;
    legs: {
      steps: {
        distance: number;
        maneuver: { instruction: string; type: string; modifier?: string };
      }[];
    }[];
  }[];
}

export class DirectionsError extends Error {}

export function parseDirections(mode: TravelMode, body: MapboxDirectionsResponse): Route {
  const route = body.routes?.[0];
  if (body.code !== 'Ok' || !route) {
    if (body.code === 'NoRoute') throw new DirectionsError('No route found between these places.');
    throw new DirectionsError(body.message || `Directions failed (${body.code}).`);
  }
  return {
    mode,
    distance: route.distance,
    duration: route.duration,
    geometry: route.geometry,
    steps: route.legs.flatMap((leg) =>
      leg.steps.map((s) => ({
        instruction: s.maneuver.instruction,
        distance: s.distance,
        type: s.maneuver.type,
        modifier: s.maneuver.modifier,
      })),
    ),
  };
}

const cache = new Map<string, Route>();

/** Mapbox Directions API (billed per request, so results are cached for the session). */
export async function fetchRoute(mode: TravelMode, from: LngLat, to: LngLat, signal?: AbortSignal): Promise<Route> {
  const coords = [from, to].map(([lng, lat]) => `${lng.toFixed(6)},${lat.toFixed(6)}`).join(';');
  const key = `${mode}/${coords}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    steps: 'true',
    language: 'en',
    access_token: import.meta.env.VITE_MAPBOX_TOKEN,
  });
  const res = await fetch(`https://api.mapbox.com/directions/v5/mapbox/${mode}/${coords}?${params}`, { signal });
  const body = (await res.json().catch(() => ({ code: `HTTP ${res.status}` }))) as MapboxDirectionsResponse;
  const route = parseDirections(mode, body);
  cache.set(key, route);
  return route;
}

// ---- Formatting (US campus: feet and miles) ------------------------------------------

const FEET_PER_METER = 3.28084;

export function formatDistance(meters: number): string {
  const feet = meters * FEET_PER_METER;
  if (feet < 1000) return `${Math.max(10, Math.round(feet / 10) * 10)} ft`;
  const miles = meters / 1609.344;
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/** Rotation (degrees) for a straight-up arrow icon, from a Mapbox maneuver modifier. */
export function maneuverRotation(modifier?: string): number {
  switch (modifier) {
    case 'sharp left':
      return -135;
    case 'left':
      return -90;
    case 'slight left':
      return -45;
    case 'slight right':
      return 45;
    case 'right':
      return 90;
    case 'sharp right':
      return 135;
    case 'uturn':
      return 180;
    default:
      return 0;
  }
}

/** Google Maps fallback link (opens the phone's maps app on mobile). */
export function googleMapsUrl(mode: TravelMode, to: LngLat, from?: LngLat): string {
  const params = new URLSearchParams({ api: '1', destination: `${to[1]},${to[0]}`, travelmode: mode });
  if (from) params.set('origin', `${from[1]},${from[0]}`);
  return `https://www.google.com/maps/dir/?${params}`;
}
